/**
 * The startup intake form (F1).
 *
 * PRD §10.1 targets under two minutes to log a startup, so only the name is
 * required. Field-level errors come from the shared schema; a rejection from
 * the API is mapped back onto the offending fields rather than shown as a
 * banner, because a form error the user cannot locate is a form they abandon.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 6.4, PRD §10.1
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { useDeferredValue, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';

import { Button } from '../../components/ui/Button';
import { Card, CardBody } from '../../components/ui/Card';
import { Field } from '../../components/ui/Field';
import { Input } from '../../components/ui/Input';
import { PageHead } from '../../components/ui/PageHead';
import { isApiError } from '../../lib/problem';
import { ROUTES, startupPath } from '../../lib/routes';

import { useCreateStartup, useStartupDuplicates } from './api/startups.queries';
import { buildDuplicateQuery } from './build-duplicate-query';
import { DuplicateWarning } from './components/DuplicateWarning';
import {
  startupFormSchema,
  type StartupFormInput,
  type StartupFormOutput,
} from './startup-form-schema';

export function StartupNewPage(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const createStartup = useCreateStartup();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setError,
    control,
    formState: { errors, isSubmitting },
  } = useForm<StartupFormInput, unknown, StartupFormOutput>({
    resolver: zodResolver(startupFormSchema),
    defaultValues: {
      name: '',
      website: '',
      hqCountry: '',
      trl: '',
      description: '',
      differentiator: '',
    },
  });

  /* Duplicate detection (F14). Watched name and website drive an advisory
     check; `useDeferredValue` lets typing stay responsive while the query
     lags behind the keystrokes rather than firing on each one. The check runs
     only once there is enough to match on — a single character is noise. */
  const watchedName = useWatch({ control, name: 'name' });
  const watchedWebsite = useWatch({ control, name: 'website' });
  const deferredName = useDeferredValue(watchedName);
  const deferredWebsite = useDeferredValue(watchedWebsite);
  const duplicateQuery = buildDuplicateQuery(deferredName, deferredWebsite);
  const { data: duplicateData } = useStartupDuplicates(duplicateQuery);

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null);

    try {
      /* Sent as-is. `zodResolver` has already validated AND applied the
         schema's transforms, so `values` is the OUTPUT shape — re-parsing it
         would feed transformed output back into the input schema and reject a
         numeric `trl` for not being the string the DOM produced. */
      const created = await createStartup.mutateAsync(values as never);
      await navigate(startupPath(created.id));
    } catch (error) {
      /* Field errors from the API are attached to their fields. A validation
         failure shown only as a banner leaves the user hunting for which input
         is wrong. */
      if (isApiError(error)) {
        /* Iterated as entries rather than keys: `fieldErrors` is built from the
           problem's own list, so every key has a string value — indexing it
           again would add an unreachable `?? ''` branch. */
        const entries = Object.entries(error.fieldErrors);

        for (const [field, message] of entries) {
          setError(field as keyof StartupFormInput, { message });
        }

        /* Only shown when nothing could be attached — otherwise the banner
           duplicates messages already beside the inputs. */
        if (entries.length === 0) {
          setSubmitError(error.problem.detail ?? t('startups.form.failed'));
        }
        return;
      }

      setSubmitError(t('startups.form.failed'));
    }
  });

  return (
    <>
      <nav aria-label={t('startup.breadcrumb')} className="text-body mb-s2 text-xs">
        {/* Persistently underlined, not only on hover: inside a text block a
            link distinguished by colour alone fails WCAG 1.4.1 (PRD §9.7). */}
        <Link to={ROUTES.startups} className="text-blue underline">
          {t('startups.title')}
        </Link>
        <span className="text-gray-200 mx-1.5">/</span>
        <span>{t('startups.form.title')}</span>
      </nav>

      <PageHead
        kicker={t('nav.group.pipeline')}
        title={t('startups.form.title')}
        lead={t('startups.form.intro')}
      />

      {duplicateData !== undefined && duplicateData.duplicates.length > 0 && (
        <div className="mb-s3 max-w-[760px]">
          <DuplicateWarning duplicates={duplicateData.duplicates} />
        </div>
      )}

      {/* `noValidate` so our own messages are shown rather than the browser's,
          which cannot be translated and cannot be announced consistently. */}
      <form
        onSubmit={(event) => {
          /* Voided: `handleSubmit` returns a promise and React expects a void
             handler. The rejection is already handled inside `onSubmit`. */
          void onSubmit(event);
        }}
        noValidate
        className="max-w-[760px]"
      >
        <Card>
          <CardBody className="gap-s3 flex flex-col">
            <Field
              label={t('startup.fields.name', 'Name')}
              hint={t('startups.form.nameHint')}
              error={errors.name?.message}
              required
            >
              {({ id, invalid, errorId, ...rest }) => (
                <Input
                  id={id}
                  invalid={invalid}
                  errorId={errorId}
                  {...rest}
                  {...register('name')}
                />
              )}
            </Field>

            <Field
              label={t('startup.fields.website')}
              hint={t('startups.form.websiteHint')}
              error={errors.website?.message}
            >
              {({ id, invalid, errorId, ...rest }) => (
                <Input
                  id={id}
                  type="url"
                  invalid={invalid}
                  errorId={errorId}
                  {...rest}
                  {...register('website')}
                />
              )}
            </Field>

            <Field
              label={t('startup.fields.country')}
              hint={t('startups.form.countryHint')}
              error={errors.hqCountry?.message}
            >
              {({ id, invalid, errorId, ...rest }) => (
                <Input
                  id={id}
                  invalid={invalid}
                  errorId={errorId}
                  maxLength={2}
                  {...rest}
                  {...register('hqCountry')}
                />
              )}
            </Field>

            <Field
              label={t('startup.fields.trl')}
              hint={t('startups.form.trlHint')}
              error={errors.trl?.message}
            >
              {({ id, invalid, errorId, ...rest }) => (
                <Input
                  id={id}
                  type="number"
                  min={1}
                  max={9}
                  invalid={invalid}
                  errorId={errorId}
                  {...rest}
                  {...register('trl')}
                />
              )}
            </Field>

            <Field
              label={t('startup.fields.description')}
              error={errors.description?.message}
            >
              {({ id, invalid, errorId, ...rest }) => (
                <Input
                  id={id}
                  invalid={invalid}
                  errorId={errorId}
                  {...rest}
                  {...register('description')}
                />
              )}
            </Field>

            <Field
              label={t('startup.fields.differentiator')}
              error={errors.differentiator?.message}
            >
              {({ id, invalid, errorId, ...rest }) => (
                <Input
                  id={id}
                  invalid={invalid}
                  errorId={errorId}
                  {...rest}
                  {...register('differentiator')}
                />
              )}
            </Field>

            {/* `role="alert"` so a submission failure is announced, not only shown. */}
            {submitError === null ? null : (
              <p role="alert" className="text-sem-bad text-sm">
                {submitError}
              </p>
            )}
          </CardBody>

          {/* The actions sit on their own strip, separated by the card's own
              rule, so "Create" is not one more control in the field stack. */}
          <div className="border-gray-100 gap-s2 flex flex-wrap border-t px-s3 py-s3">
            <Button type="submit" variant="action" disabled={isSubmitting}>
              {isSubmitting ? t('startups.form.submitting') : t('startups.form.submit')}
            </Button>
            <Button variant="secondary" asChild>
              <Link to={ROUTES.startups}>{t('actions.cancel')}</Link>
            </Button>
          </div>
        </Card>
      </form>
    </>
  );
}
